import { EmailOutlined, Home, LockOutlined, PersonOutline, PublicOutlined } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { FormSection, SelectInput, StatusPill, StyledCard, TextInput } from "components";
import { ENDPOINTS, LANGUAGES } from "config/constants";
import { useContextController } from "context";
import { changeLocale, login } from "context/actions";
import { useFetch, useTitle } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { useSnackbar } from "notistack";
import { ChangeEventHandler, FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { UserModel, UserType } from "types";
import { validateName, validatePassword } from "utils";
import ProfilePicture from "./image";

const defaultState = {
  email: "",
  name: "",
  password: "",
  newPassword: "",
  confirmPassword: ""
};

const fieldSx = { height: 48, minHeight: 48 };
const iconSx = { fontSize: 20, color: COLORS.surface.secondaryText };

const primaryBtnSx = {
  minWidth: 140,
  height: 44,
  borderRadius: `${COLORS.radius.md}px`,
  fontWeight: 500,
  textTransform: "none",
  ...primaryContainedButtonSx
};

interface Data {
  user?: UserModel;
  errors?: typeof defaultState;
}

function getUserTypeLabel(type: UserType | undefined, t: Record<string, string>) {
  if (type === "super") return t["Super Admin"];
  if (type === "admin") return t["Admin"];
  return t["User"];
}

export default function Profile() {
  const t = useTranslation();
  const [state, setState] = useState(defaultState);
  const [errors, setErrors] = useState(defaultState);
  const [lang, setLang] = useState(localStorage.getItem("locale") === "fr" ? "Français" : "English");

  const { isLoading, response, error, callFetch: callFetchPut } = useFetch<Data>("PUT");
  const { isLoading: isLoadingGet, response: responseGet, callFetch: callFetchGet } = useFetch<Data>("GET", true);
  const [{ userData }, dispatch] = useContextController();
  const { enqueueSnackbar } = useSnackbar();

  const profileDirty = state.name !== userData?.name;
  const passwordDirty = Boolean(state.password || state.newPassword || state.confirmPassword);
  const passwordValid = useMemo(
    () =>
      Boolean(
        state.password &&
          state.newPassword &&
          state.confirmPassword &&
          !errors.password &&
          !errors.newPassword &&
          !errors.confirmPassword
      ),
    [state, errors]
  );

  const onSubmitProfile: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (state.name === userData?.name) {
        enqueueSnackbar(t["Nothing to update"], { variant: "info" });
        return;
      }
      callFetchPut(ENDPOINTS.account, { body: JSON.stringify({ name: state.name }) }).catch(console.error);
    },
    [state.name, userData?.name, enqueueSnackbar, callFetchPut, t]
  );

  const onSubmitPassword: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPut(ENDPOINTS.account, {
        body: JSON.stringify({
          password: state.password,
          newPassword: state.newPassword
        })
      }).catch(console.error);
    },
    [state.password, state.newPassword, callFetchPut]
  );

  const handleNameChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setState((prevState) => ({ ...prevState, name: e.target.value }));
    setErrors((prevState) => ({ ...prevState, name: validateName(e.target.value) }));
  }, []);

  const handlePasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setState((prevState) => ({ ...prevState, password: e.target.value }));
    setErrors((prevState) => ({ ...prevState, password: "" }));
  }, []);

  const handleNewPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setState((prevState) => ({ ...prevState, newPassword: e.target.value }));
    setErrors((prevState) => ({ ...prevState, newPassword: validatePassword(e.target.value) }));
  }, []);

  const handleConfirmPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setState((prevState) => ({ ...prevState, confirmPassword: e.target.value }));
      setErrors((prevState) => ({
        ...prevState,
        confirmPassword: e.target.value !== state.newPassword ? t["Passwords do not match"] : ""
      }));
    },
    [state.newPassword, t]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      if (response.data?.user) {
        login(response.data.user, dispatch);
        const formElem = document.getElementById("password-form") as HTMLFormElement | null;
        formElem?.reset();
        setState((prevState) => ({
          ...prevState,
          ...response.data?.user,
          password: "",
          newPassword: "",
          confirmPassword: ""
        }));
        setErrors(defaultState);
      }
    }
  }, [response, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({ ...prevState, ...error.data.errors }));
      }
    }
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    if (responseGet?.data?.user) {
      login(responseGet.data.user, dispatch);
      setState((prevState) => ({ ...prevState, ...responseGet.data?.user }));
    }
  }, [responseGet, dispatch]);

  useEffect(() => {
    callFetchGet(ENDPOINTS.account).catch(console.error);
  }, [callFetchGet]);

  useTitle("Profile");

  return (
    <PrivateLayout canAccess={() => true}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Tooltip title={t["Home"]}>
          <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
            <Home fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography color="text.primary">{t["Profile"]}</Typography>
      </Breadcrumbs>

      <Box mb={3}>
        <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 24, lineHeight: 1.3 }}>
          {t["My Profile"]}
        </Typography>
        <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14, mt: 0.5, lineHeight: 1.5 }}>
          {t["My Profile description"]}
        </Typography>
      </Box>

      {isLoadingGet ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: `${COLORS.radius.md}px` }} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: `${COLORS.radius.md}px` }} />
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: `${COLORS.radius.md}px` }} />
            </Stack>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={4} lg={3.5}>
            <StyledCard>
              <Stack alignItems="center" spacing={1.5} sx={{ px: 3, py: 3 }}>
                <ProfilePicture />
                <Box textAlign="center" width="100%">
                  <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 18 }}>
                    {userData?.name || "—"}
                  </Typography>
                  <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14, mt: 0.25 }}>
                    {userData?.email || "—"}
                  </Typography>
                  {userData?.type ? (
                    <Box mt={1.25}>
                      <StatusPill label={getUserTypeLabel(userData.type, t)} status={userData.type} />
                    </Box>
                  ) : null}
                </Box>
              </Stack>
              <Divider sx={{ borderColor: COLORS.surface.border }} />
              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography sx={{ color: COLORS.surface.primaryText, fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                  {t["Language"]}
                </Typography>
                <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 13, mb: 1.5, lineHeight: 1.5 }}>
                  {t["Language helper"]}
                </Typography>
                <SelectInput
                  id="profile-language"
                  data={LANGUAGES.map((item) => ({ label: item, value: item }))}
                  value={lang}
                  startAdornment={<PublicOutlined sx={iconSx} />}
                  onChange={({ target: { value } }) => {
                    if (!value) return;
                    setLang(value as string);
                    changeLocale(value === "English" ? "en" : "fr", dispatch);
                  }}
                  fullWidth
                />
              </Box>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={8} lg={8.5}>
            <Stack spacing={2}>
              <FormSection
                title={t["Personal Information"]}
                description={t["Personal Information description"]}
                sx={{ mb: 0 }}
              >
                <Box component="form" onSubmit={onSubmitProfile}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        fullWidth
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="username"
                        label={t["Email"]}
                        value={state.email}
                        disabled
                        startAdornment={<EmailOutlined sx={iconSx} />}
                        endAdornment={<LockOutlined sx={iconSx} />}
                        helperText={t["Email cannot be changed"]}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextInput
                        fullWidth
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        label={t["Name"]}
                        placeholder={t["e.g John Doe"]}
                        helperText={errors.name}
                        error={!!errors.name}
                        value={state.name}
                        onChange={handleNameChange}
                        startAdornment={<PersonOutline sx={iconSx} />}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                  <Box display="flex" justifyContent="flex-end" mt={2.5}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!profileDirty || !!errors.name || isLoading}
                      endIcon={isLoading && profileDirty ? <CircularProgress size={18} /> : undefined}
                      sx={primaryBtnSx}
                    >
                      {t["Save Changes"]}
                    </Button>
                  </Box>
                </Box>
              </FormSection>

              <FormSection title={t["Password & Security"]} description={t["Password & Security description"]} sx={{ mb: 0 }}>
                <Box component="form" id="password-form" onSubmit={onSubmitPassword}>
                  <input hidden readOnly autoComplete="username" value={state.email} />
                  <Stack spacing={2}>
                    <TextInput
                      required
                      fullWidth
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      label={t["Current Password"]}
                      placeholder="**********"
                      helperText={errors.password}
                      error={!!errors.password}
                      onChange={handlePasswordChange}
                      startAdornment={<LockOutlined sx={iconSx} />}
                      sx={fieldSx}
                    />
                    <Box>
                      <TextInput
                        required
                        fullWidth
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        autoComplete="new-password"
                        label={t["New Password"]}
                        placeholder="**********"
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        onChange={handleNewPasswordChange}
                        startAdornment={<LockOutlined sx={iconSx} />}
                        sx={fieldSx}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: COLORS.surface.secondaryText, display: "block", mt: 0.75, lineHeight: 1.6 }}
                      >
                        {t["Password requirements"]}
                      </Typography>
                    </Box>
                    <TextInput
                      required
                      fullWidth
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="**********"
                      label={t["Confirm New Password"]}
                      helperText={errors.confirmPassword}
                      error={!!errors.confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      startAdornment={<LockOutlined sx={iconSx} />}
                      sx={fieldSx}
                    />
                  </Stack>
                  <Box display="flex" justifyContent="flex-end" mt={2.5}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!passwordValid || isLoading}
                      endIcon={isLoading && passwordDirty ? <CircularProgress size={18} /> : undefined}
                      sx={primaryBtnSx}
                    >
                      {t["Change Password"]}
                    </Button>
                  </Box>
                </Box>
              </FormSection>
            </Stack>
          </Grid>
        </Grid>
      )}
    </PrivateLayout>
  );
}
